// cost/CostDivergenceBadge.tsx
import React from 'react';
import { costSeverity } from './costSeverity';
import type { CostTelemetry, OvershootEvent } from '../../../../types/council';

function divergenceTitle(t: CostTelemetry): string {
  // Wire cost_divergence_ratio = (estimate − actual) / actual — a SIGNED delta ratio, NOT a
  // multiplier. Render it as a signed % (its true meaning); "N×" would lie for undercounts
  // (ratio −0.9 = a 10× undercount, would have printed "0.90×"). No recompute beyond *100.
  const r = t.cost_divergence_ratio != null ? parseFloat(t.cost_divergence_ratio) : NaN;
  const pct = Number.isFinite(r) ? `${r >= 0 ? '+' : ''}${(r * 100).toFixed(0)}%` : '?';
  const model = t.model_slug ? ` · ${t.model_slug}` : '';
  return `estimate off by ${pct} (est $${t.cost_usd} → act $${t.cost_actual_usd})${model}`;
}
function breachTitle(o: OvershootEvent): string {
  const by = (parseFloat(o.used_after) - parseFloat(o.scope_cap)).toFixed(6);
  return `over ${o.scope} cap by $${by} (used $${o.used_after} / cap $${o.scope_cap})`;
}

export function CostDivergenceBadge(
  { telemetry, overshoot }: { telemetry?: CostTelemetry | null; overshoot?: OvershootEvent | null },
) {
  const sev = costSeverity(telemetry ?? null, !!overshoot);
  if (!sev.divergent && !sev.breached) {
    return <span className="inline-block w-[14px]" aria-hidden />;  // reserved slot → no canvas reflow
  }
  const title = [
    sev.breached && overshoot ? breachTitle(overshoot) : '',
    sev.divergent && telemetry ? divergenceTitle(telemetry) : '',
  ].filter(Boolean).join('\n');
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold leading-none"
      title={title} data-testid="cost-divergence-badge" aria-label={title}>
      {sev.breached && <span className="text-red-600 dark:text-red-400">▲</span>}
      {sev.divergent && <span className="text-amber-700 dark:text-amber-300">Δ</span>}{/* amber-700: readable on white peer AND amber-50 merge node */}
    </span>
  );
}
